import { createContext, useContext, useReducer, useMemo } from "react";

const StreakContext = createContext(null);

const initialStreakState = {
  streakActive: false,
  streakStartAt: null,
  streakEndAt: null,
  history: [],
};

// Ensure streak state is valid and has no null/undefined values
function validateStreakState(state) {
  if (!state || typeof state !== 'object') {
    return { ...initialStreakState };
  }
  return {
    streakActive: state?.streakActive ?? false,
    streakStartAt: state?.streakStartAt ?? null,
    streakEndAt: state?.streakEndAt ?? null,
    history: Array.isArray(state?.history) ? state.history : [],
  };
}

function streakReducer(state, action) {
  switch (action.type) {
    case "START_STREAK":
      return {
        ...state,
        streakActive: true,
        streakStartAt: action.payload.startAt,
        streakEndAt: null,
      };

    case "END_STREAK": {
      const endAt = new Date().toISOString();
      const { slipReasons, slipNotes } = action.payload;

      return {
        ...state,
        history: [
          ...state.history,
          {
            startAt: state.streakStartAt,
            endAt: endAt,
            durationMs: state.streakStartAt
              ? Date.now() - new Date(state.streakStartAt).getTime()
              : 0,
            slipReasons,
            slipNotes,
          },
        ],
        streakActive: false,
        streakStartAt: null,
        streakEndAt: endAt,
      };
    }

    case "HYDRATE_STREAK":
      return action.payload;

    default:
      return state;
  }
}

export function StreakProvider({ children, initialState = initialStreakState }) {
  // Validate and sanitize initial state
  const validatedState = useMemo(() => validateStreakState(initialState), [initialState]);
  const [state, dispatch] = useReducer(streakReducer, validatedState);

  // Memoize computed values
  const currentDurationMs = useMemo(() => {
    if (!state.streakActive || !state.streakStartAt) return 0;
    return Date.now() - new Date(state.streakStartAt).getTime();
  }, [state.streakActive, state.streakStartAt]);

  const lastDurationMs = useMemo(() => {
    if (!Array.isArray(state?.history) || state.history.length === 0) return 0;
    const lastEntry = state.history[state.history.length - 1];
    return lastEntry?.durationMs ?? 0;
  }, [state?.history]);

  const bestDurationMs = useMemo(() => {
    if (!Array.isArray(state?.history) || state.history.length === 0) return 0;
    const durations = state.history.map((h) => h?.durationMs ?? 0);
    return durations.length > 0 ? Math.max(...durations) : 0;
  }, [state?.history]);

  const actions = {
    startStreak: () => {
      dispatch({
        type: "START_STREAK",
        payload: { startAt: new Date().toISOString() },
      });
    },

    endStreak: (payload) => {
      dispatch({
        type: "END_STREAK",
        payload,
      });
    },

    hydrateStreak: (savedState) => {
      dispatch({
        type: "HYDRATE_STREAK",
        payload: savedState,
      });
    },
  };

  // Memoize provider value
  const value = useMemo(
    () => ({
      state,
      actions,
      currentDurationMs,
      lastDurationMs,
      bestDurationMs,
    }),
    [state, currentDurationMs, lastDurationMs, bestDurationMs]
  );

  return (
    <StreakContext.Provider value={value}>{children}</StreakContext.Provider>
  );
}

export function useStreakContext() {
  const context = useContext(StreakContext);
  if (!context) {
    throw new Error("useStreakContext must be used within StreakProvider");
  }
  return context;
}
